import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BacktestChart from '../components/BacktestChart';

function DemoBacktest() {
  const [amount, setAmount] = useState('');
  const [pair, setPair] = useState('XAUUSD');
  const [riskLevel, setRiskLevel] = useState('Medium');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [animatedTrades, setAnimatedTrades] = useState([]);

  const tradingPairs = [
    { value: 'XAUUSD', label: 'XAUUSD (Gold)', avgReturn: 8.5 },
    { value: 'NAS100', label: 'NAS100 (NASDAQ)', avgReturn: 12.3 },
    { value: 'GER30', label: 'GER30 (DAX)', avgReturn: 9.7 },
    { value: 'SP500', label: 'S&P 500', avgReturn: 10.2 },
    { value: 'GBPJPY', label: 'GBPJPY', avgReturn: 11.5 },
    { value: 'EURUSD', label: 'EURUSD', avgReturn: 7.8 }
  ];

  const riskLevels = {
    Low: { multiplier: 0.7, label: 'Low (5%)', winRate: 68 },
    Medium: { multiplier: 1.0, label: 'Medium (10%)', winRate: 72 },
    High: { multiplier: 1.4, label: 'High (15%)', winRate: 65 }
  };

  const runBacktest = () => {
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setResults(null);
    setChartData(null);
    setAnimatedTrades([]);

    // Simulate backtest calculation
    setTimeout(() => {
      const selectedPair = tradingPairs.find(p => p.value === pair);
      const risk = riskLevels[riskLevel];
      
      // Calculate results
      const baseReturn = selectedPair.avgReturn * risk.multiplier;
      const monthlyReturn = (parseFloat(amount) * baseReturn) / 100;
      const finalAmount = parseFloat(amount) + monthlyReturn;
      
      // Generate realistic price chart data (30 days)
      const dataPoints = 120; // 120 points for 30 days
      const priceData = [];
      let currentPrice = 1000; // Base price
      
      for (let i = 0; i < dataPoints; i++) {
        // Random walk with slight upward bias
        const change = (Math.random() - 0.48) * 20; // Slightly bullish
        currentPrice += change;
        priceData.push({
          x: i,
          y: Math.max(currentPrice, 900), // Keep price above 900
          time: `Day ${Math.floor(i / 4)}`
        });
      }

      // Generate realistic trade data with chart positions
      const trades = [];
      const numTrades = Math.floor(Math.random() * 8) + 15; // 15-22 trades
      let wins = 0;
      
      for (let i = 0; i < numTrades; i++) {
        const isWin = Math.random() * 100 < risk.winRate;
        if (isWin) wins++;
        
        const profitLoss = isWin 
          ? (Math.random() * 150 + 80) * (parseFloat(amount) / 10000)
          : -(Math.random() * 80 + 30) * (parseFloat(amount) / 10000);
        
        // Assign random position on chart with bounds checking
        const entryIndex = Math.floor(Math.random() * (dataPoints - 25)) + 5;
        const exitIndex = Math.min(entryIndex + Math.floor(Math.random() * 15) + 5, dataPoints - 1);
        
        // Validate indices
        if (entryIndex >= priceData.length || exitIndex >= priceData.length) continue;
        
        trades.push({
          id: i + 1,
          pair: pair,
          type: isWin ? 'WIN' : 'LOSS',
          profit: profitLoss.toFixed(2),
          entryIndex,
          exitIndex,
          entryPrice: priceData[entryIndex].y,
          exitPrice: priceData[exitIndex].y
        });
      }

      setChartData(priceData);
      setResults({
        startingAmount: parseFloat(amount),
        finalAmount: finalAmount.toFixed(2),
        profit: monthlyReturn.toFixed(2),
        returnPercentage: baseReturn.toFixed(2),
        totalTrades: numTrades,
        winningTrades: wins,
        losingTrades: numTrades - wins,
        winRate: ((wins / numTrades) * 100).toFixed(1),
        pair: selectedPair.label,
        riskLevel: risk.label,
        trades: trades
      });

      setLoading(false);

      // Animate trades appearing on chart
      trades.forEach((trade, index) => {
        setTimeout(() => {
          setAnimatedTrades(prev => [...prev, trade]);
        }, index * 200); // 200ms delay between each trade
      });
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-full bg-[#00FF7F] flex items-center justify-center">
              <span className="text-black font-bold text-sm">JG</span>
            </div>
            <span className="font-light text-lg tracking-wide">JOHN GHATTI</span>
          </Link>
          <Link
            to="/"
            className="px-4 py-2 border border-zinc-700 hover:border-[#00FF7F] rounded-lg text-sm transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-light mb-4">
              Kasango AI Pro <span className="text-[#00FF7F]">Demo Backtest</span>
            </h1>
            <p className="text-gray-400 text-lg font-extralight max-w-2xl mx-auto">
              See how Kasango AI Pro would have performed with your investment. Real historical data, real results.
            </p>
          </div>

          {/* Chart Section - Full Width */}
          {chartData && (
            <div className="mb-8 animate-fade-in">
              <BacktestChart 
                data={chartData} 
                trades={animatedTrades} 
                pair={pair}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8">
              <h2 className="text-2xl font-light mb-6">Configure Your Test</h2>
              
              {/* Amount Input */}
              <div className="mb-6">
                <label className="block text-sm font-light text-gray-400 mb-2">
                  Investment Amount (R)
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g., 10000"
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:border-[#00FF7F] focus:outline-none transition-colors"
                />
                <p className="text-xs text-gray-500 mt-2">Minimum: R5,000 | Recommended: R10,000+</p>
              </div>

              {/* Trading Pair */}
              <div className="mb-6">
                <label className="block text-sm font-light text-gray-400 mb-2">
                  Select Trading Pair
                </label>
                <select
                  value={pair}
                  onChange={(e) => setPair(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:border-[#00FF7F] focus:outline-none transition-colors"
                >
                  {tradingPairs.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Risk Level */}
              <div className="mb-8">
                <label className="block text-sm font-light text-gray-400 mb-2">
                  Risk Level
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {Object.keys(riskLevels).map((level) => (
                    <button
                      key={level}
                      onClick={() => setRiskLevel(level)}
                      className={`py-3 px-4 rounded-xl font-light text-sm transition-all ${
                        riskLevel === level
                          ? 'bg-[#00FF7F] text-black'
                          : 'bg-zinc-900 border border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Run Button */}
              <button
                onClick={runBacktest}
                disabled={loading}
                className="w-full py-4 bg-[#00FF7F] text-black font-medium rounded-xl hover:bg-[#00DD6F] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Running Backtest...' : 'Run Demo Backtest'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                ⚡ Demo uses historical data from last 30 days
              </p>
            </div>

            {/* Results Section */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8">
              <h2 className="text-2xl font-light mb-6">Results</h2>

              {!results && !loading && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <p className="text-gray-400 font-extralight">
                    Configure your test and click "Run Demo Backtest" to see results
                  </p>
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full border-4 border-zinc-700 border-t-[#00FF7F] animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-400 font-light">Analyzing {pair}...</p>
                  <p className="text-sm text-gray-500 mt-2">Processing 30-day historical data</p>
                </div>
              )}

              {results && (
                <div className="space-y-6">
                  {/* Profit Summary */}
                  <div className="bg-gradient-to-br from-[#00FF7F]/10 to-transparent border border-[#00FF7F]/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-400">Total Profit</span>
                      <span className="text-2xl font-bold text-[#00FF7F]">
                        R{results.profit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Return</span>
                      <span className="text-[#00FF7F] font-medium">+{results.returnPercentage}%</span>
                    </div>
                  </div>

                  {/* Account Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-900 rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">Starting</p>
                      <p className="text-lg font-medium">R{results.startingAmount}</p>
                    </div>
                    <div className="bg-zinc-900 rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">Final Amount</p>
                      <p className="text-lg font-medium text-[#00FF7F]">R{results.finalAmount}</p>
                    </div>
                  </div>

                  {/* Trade Statistics */}
                  <div>
                    <h3 className="text-sm font-light text-gray-400 mb-3">Trade Statistics</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Total Trades</span>
                        <span className="font-medium">{results.totalTrades}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Winning Trades</span>
                        <span className="font-medium text-green-400">{results.winningTrades}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Losing Trades</span>
                        <span className="font-medium text-red-400">{results.losingTrades}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Win Rate</span>
                        <span className="font-medium text-[#00FF7F]">{results.winRate}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Top 5 Trades */}
                  <div>
                    <h3 className="text-sm font-light text-gray-400 mb-3">Top 5 Trades</h3>
                    <div className="space-y-2">
                      {results.trades
                        .sort((a, b) => Math.abs(parseFloat(b.profit)) - Math.abs(parseFloat(a.profit)))
                        .slice(0, 5)
                        .map((trade) => (
                        <div key={trade.id} className="flex items-center justify-between bg-zinc-900 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500">#{trade.id}</span>
                            <span className="text-sm">{trade.pair}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              trade.type === 'WIN' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {trade.type}
                            </span>
                            <span className={`text-sm font-medium ${
                              parseFloat(trade.profit) > 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {parseFloat(trade.profit) > 0 ? '+' : ''}R{trade.profit}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <Link
                    to="/"
                    className="block w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl text-center font-medium transition-all"
                  >
                    Get Kasango AI Pro Now →
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 text-center">
            <p className="text-xs text-gray-500 max-w-2xl mx-auto">
              ⚠️ Demo results are based on historical data and simulated performance. Past performance is not indicative of future results. 
              Actual trading involves risk and you should only trade with money you can afford to lose.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoBacktest;
